import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isArray, reduce, filter, find, sumBy, range, shuffle, concat } from 'lodash';
import moment from 'moment';

import GodTable, { CUSTOM_FIELD_TYPES } from '../../../../components/god-table';
import Chart from './chart';

// Import Selectors
import { getTablePreference } from '../../../App/AppReducer';
import { getCanAccessToSalesforce } from '../../../Auth/AuthReducer';
import ArrayRenderer from '../../../../components/table-cells/ArrayRenderer';
import OpportunityNameRenderer from '../../../../components/table-cells/OpportunityNameRenderer';
import SaleRepsNameRenderer from '../../../../components/table-cells/SaleRepsNameRenderer';
import ForecastAmountRenderer from './ForecastAmountRenderer';
import ForecastStageRenderer from './ForecastStageRenderer';
import ForecastCategoryRenderer from './ForecastCategoryRenderer';
import ForecastCloseDateRenderer from './ForecastCloseDateRenderer';
import MoneyRenderer from '../../../../components/table-cells/MoneyRenderer';
import NumberRenderer from '../../../../components/table-cells/NumberRenderer';
import AgeRenderer from '../../../../components/table-cells/AgeRenderer';
import DateRenderer from '../../../../components/table-cells/DateRenderer';
import {
  dateComparator, minDateAggregation, stringComparator,
} from '../../../../components/table-cells/helpers';
import styles from './styles.css';

const highlightBg = 'rgba(33, 150, 243, 0.1)';

class OpportunityTable extends Component {
  constructor(props) {
    super(props);
    this.state = {
      groupingByCloseDate: false,
      closeDateGroupingType: 'monthly',
      dealType: null,
    };
    this.hanldeOnColumnRowGroupChanged = this.hanldeOnColumnRowGroupChanged.bind(this);
    this.onGridReady = this.onGridReady.bind(this);
    this.getMainMenuItems = this.getMainMenuItems.bind(this);
    this.getRowStyle = this.getRowStyle.bind(this);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.dealType !== this.props.dealType) {
      this.setState({ dealType: nextProps.dealType });
    }
  }

  onGridReady(params) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    params.api.addAggFunc('minDate', minDateAggregation);
  }

  getDealTypes() {
    const { dealType } = this.state;
    const { stats } = this.props;
    const allDealTypes = [...filter(stats.dealTypes, d => d.count > 0) || []];
    const totalMenu = { label: 'All Opportunities', value: null, count: sumBy(allDealTypes, 'count') };
    const active = find(allDealTypes, { value: dealType }) || totalMenu;
    return {
      name: `Showing: ${active.label}`,
      checked: active !== undefined,
      subMenu: concat([totalMenu, 'separator'], allDealTypes).map((stat) => {
        if (stat !== 'separator') {
          return {
            name: stat.label,
            checked: stat.value === dealType,
            action: () => { this.setState({ dealType: stat.value }); },
          };
        }
        return stat;
      }),
    };
  }

  getMainMenuItems(params) {
    const { groupingByCloseDate, closeDateGroupingType } = this.state;
    const columnId = params.column.getId();
    if (groupingByCloseDate && columnId === 'ag-Grid-AutoColumn') {
      const menuItems = params.defaultItems.slice(0);
      const subMenus = [
        { name: 'Monthly', value: 'monthly' },
        { name: 'Quarterly', value: 'quarterly' },
        { name: 'Yearly', value: 'yearly' },
      ];
      menuItems.push({
        name: 'Group By Close Date',
        checked: groupingByCloseDate,
        subMenu: subMenus.map(item => ({
          ...item,
          checked: item.value === closeDateGroupingType,
          action: () => {
            this.setState(
              { closeDateGroupingType: item.value },
              () => {
                if (this.gridApi) {
                  this.gridApi.refreshClientSideRowModel('group');
                }
              },
            );
          },
        })),
      });
      return menuItems;
    } else if (params.column.colDef.cellRendererParams.column && params.column.colDef.cellRendererParams.column.tag === 'opportunityType') {
      const menuItems = params.defaultItems.slice(0);
      menuItems.unshift('separator');
      menuItems.unshift(this.getDealTypes());
      return menuItems;
    }
    return params.defaultItems;
  }

  getRowStyle(params) {
    const { node: { data } } = params;
    if (data && data.is_untracked) {
      return { background: highlightBg };
    }
    return {};
  }

  getCloseDateColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'opportunity_close_date',
      sort: 'desc',
      type: 'dateColumn',
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'forecastCloseDateRenderer',
        };
      },
      enableRowGroup: true,
      cellRendererParams: {
        isCloseDate: true,
        column,
      },
      keyCreator: (params) => {
        const vals = params.value;
        if (!vals) {
          return '--';
        }
        let format = 'Y \\QQ';
        if (this.state.closeDateGroupingType === 'quarterly') {
          format = 'Y \\QQ';
        } else if (this.state.closeDateGroupingType === 'monthly') {
          format = 'Y MM';
        } else if (this.state.closeDateGroupingType === 'yearly') {
          format = 'Y';
        }
        return moment(vals).format(format);
      },
      valueFormatter: (params) => {
        if (params.value) {
          let format = 'Y \\QQ';
          let displayFormat = '\\QQ Y';
          if (this.state.closeDateGroupingType === 'quarterly') {
            format = 'Y \\QQ';
            displayFormat = '\\QQ Y';
          } else if (this.state.closeDateGroupingType === 'monthly') {
            format = 'Y MM';
            displayFormat = 'MMMM Y';
          } else if (this.state.closeDateGroupingType === 'yearly') {
            format = 'Y';
            displayFormat = 'Y';
          }
          return moment(`${params.value}`, format).format(displayFormat);
        }
        return params.value;
      },
    };
  }

  getNameColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'name',
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      comparator: stringComparator,
      cellRenderer: 'opportunityNameRenderer',
      cellRendererParams: {
        enableAutoHeight: true,
        column,
        exportURLType: 'opportunity',
      },
    };
  }

  getAmountColumn(column) {
    const field = this.props.isBeginning ? 'amount' : 'opportunity_amount';
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field,
      // type: 'numericColumn',
      cellClass: 'text-left', // PI asked for this
      cellRenderer: 'forecastAmountRenderer',
      cellRendererParams: {
        field,
        column,
      },
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
    };
  }

  getWeightedAmountColumn(column) {
    const { isBeginning } = this.props;
    const field = isBeginning ? 'begingningWeightedForecast' : 'weightedForecast';
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field,
      type: 'numericColumn',
      cellRenderer: 'moneyRenderer',
      cellRendererParams: {
        field,
        column,
        precision: 2,
      },
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
    };
  }

  getTypeWeightingColumn(column) {
    const field = 'winAmountProbabilityRate';
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field,
      type: 'numericColumn',
      cellRenderer: 'numberRenderer',
      valueGetter: (params) => {
        if (!params || !params.data || params.node.group) {
          return null;
        }
        const { data } = params;
        const value = data.winAmountProbabilityRate;
        if (Number.isNaN(value)) {
          return null;
        }
        return value * 100;
      },
      cellRendererParams: {
        field,
        column,
        suffix: '%',
        precision: 1,
      },
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
    };
  }

  getStageColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      enablePivot: true,
      enableRowGroup: true,
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
      valueGetter: (params) => {
        if (!params || !params.data || params.node.group) {
          return null;
        }
        const { data } = params;
        return data.is_closed ? data.stage : data.stage_name;
      },
      cellRendererSelector: (params) => {
        if (!params) {
          return null;
        }
        return {
          component: 'forecastStageRenderer',
        };
      },
    };
  }

  getProductColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'products',
      cellRenderer: 'arrayRenderer',
      cellClass: column.auto_height ? 'cell-wrap-text' : null,
      autoHeight: column.auto_height,
      cellRendererParams: {
        enableAutoHeight: true,
        column,
      },
    };
  }

  getAgeColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'pursuit_length',
      cellRenderer: 'ageRenderer',
      type: 'numericColumn',
      enableValue: true,
      allowedAggFuncs: ['sum', 'min', 'max', 'avg'],
      cellRendererParams: {
        column,
      },
    };
  }

  getOpportunityTypeColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'types',
      enableRowGroup: true,
      filter: false, // we use our own filter
      menuTabs: ['generalMenuTab', 'columnsMenuTab'],  // we use our own filter
      valueGetter: (params) => {
        if (!params || !params.data || params.node.group) {
          return null;
        }
        const { data } = params;
        const types = data.types || [];

        const allTypes = isArray(types) ? types : [types];
        return allTypes.join(', ');
      },
      cellRendererParams: {
        column,
      },
    };
  }

  getSalesRepColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'owner.name',
      enableRowGroup: true,
      enablePivot: true,
      cellRenderer: 'saleRepsNameRenderer',
      keyCreator: (params) => {
        const vals = params.value;
        if (!vals || (vals && vals.length === 0)) {
          return '--';
        }
        return vals;
      },
      cellRendererParams: {
        column,
      },
    };
  }

  getCategoryColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'opportunity_forecast',
      enableRowGroup: true,
      enablePivot: true,
      cellRenderer: 'forecastCategoryRenderer',
      valueGetter: (params) => {
        if (!params || !params.data || params.node.group) {
          return null;
        }
        return params.data.opportunity_forecast || params.data.forecast_category_name;
      },
      cellRendererParams: {
        column,
      },
    };
  }

  getSalesRegionColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'rep_regions',
      cellRenderer: 'arrayRenderer',
      cellRendererParams: {
        column,
      },
    };
  }

  getSalesTypeColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: 'rep_types',
      cellRenderer: 'arrayRenderer',
      cellRendererParams: {
        column,
      },
    };
  }
  
  getRequestedFeedbackColumn(column) {
    return {
      colId: column._id,
      headerName: column.label,
      width: column.width,
      hide: !column.is_visible,
      field: column.target_field,
      cellRenderer: 'requestedFeedbackRenderer',
      cellRendererParams: {
        column,
      },
    };
  }

  getColDefs() {
    const { tableColumns, isWeighted } = this.props;
    return reduce(tableColumns, (columns, tableColumn) => {
      let column = null;
      if (tableColumn.tag === 'salesRep') {
        column = this.getSalesRepColumn(tableColumn);
      } else if (tableColumn.tag === 'opportunityName') {
        column = this.getNameColumn(tableColumn);
      } else if (tableColumn.tag === 'opportunityAmount') {
        column = this.getAmountColumn(tableColumn);
      } else if (tableColumn.tag === 'opportunityType') {
        column = this.getOpportunityTypeColumn(tableColumn);
      } else if (tableColumn.tag === 'closeDate') {
        column = this.getCloseDateColumn(tableColumn);
      } else if (tableColumn.tag === 'stage') {
        column = this.getStageColumn(tableColumn);
      } else if (tableColumn.tag === 'age') {
        column = this.getAgeColumn(tableColumn);
      } else if (tableColumn.tag === 'category') {
        column = this.getCategoryColumn(tableColumn);
      } else if (tableColumn.tag === 'salesRegion') {
        column = this.getSalesRegionColumn(tableColumn);
      } else if (tableColumn.tag === 'salesType') {
        column = this.getSalesTypeColumn(tableColumn);
      } else if (tableColumn.tag === 'products') {
        column = this.getProductColumn(tableColumn);
      } else if (tableColumn.tag === 'weightedAmount') {
        if (isWeighted) {
          column = this.getWeightedAmountColumn(tableColumn);
        }
      } else if (tableColumn.tag === 'typeWeighting') {
        if (isWeighted) {
          column = this.getTypeWeightingColumn(tableColumn);
        }
      } else if (tableColumn.tag === 'opportunityDetails') {
        column = null; // old table version
      } else if (tableColumn.tag === 'salesDetails') {
        column = null; // old tableversion
      } else if (tableColumn.target_object === 'Opportunity' && tableColumn.target_field === 'requested_feedbacks') {
        column = this.getRequestedFeedbackColumn(tableColumn);
      } else {
        column = {
          column: tableColumn,
          customFieldType: CUSTOM_FIELD_TYPES.OPPORTUNITY,
        };
      }
      if (column) {
        columns.push(column);
      }
      return columns;
    }, []);
  }

  hanldeOnColumnRowGroupChanged(event) {
    const { columns } = event;
    const closeDateColumn = find(columns, col => col.colDef && col.colDef.cellRendererParams && col.colDef.cellRendererParams.isCloseDate);
    this.setState({ groupingByCloseDate: closeDateColumn !== undefined });
  }

  renderCharts() {
    const {
      showTypeChart, showCategoryChart, showSizeChart, showCycleChart, stats,
    } = this.props;
    const hasCharts = showTypeChart || showCategoryChart || showSizeChart || showCycleChart;
    if (!hasCharts) {
      return null;
    }
    const charts = [];
    if (showTypeChart) {
      charts.push({ title: 'Deal Type', stats: stats.dealTypes });
    }
    if (showCategoryChart) {
      charts.push({ title: 'Category', stats: stats.forecasts });
    }
    if (showSizeChart) {
      charts.push({ title: 'Deal Size', stats: stats.sizes });
    }
    if (showCycleChart) {
      charts.push({ title: 'Sales Cycle Length', stats: stats.cycles });
    }
    const col = 12 / charts.length;
    const indices = shuffle(range(charts.length));

    return (
      <div className="row">
        {charts.map((chart, idx) => (
          <div key={idx} className={`col-sm-6 col-md-${col}`}>
            <Chart title={chart.title} stats={chart.stats} idx={indices[idx]} />
          </div>
          ))}
      </div>
    );
  }



  renderTable(items) {
    const columnDefs = this.getColDefs();
    return (
      <GodTable
        id={this.props.tableName}
        columnDefs={columnDefs}
        rowData={items}
        onGridReady={this.onGridReady}
        getRowStyle={this.getRowStyle}
        context={this.props.context}
        frameworkComponents={{
          opportunityNameRenderer: OpportunityNameRenderer,
          ageRenderer: AgeRenderer,
          dateRenderer: DateRenderer,
          arrayRenderer: ArrayRenderer,
          saleRepsNameRenderer: SaleRepsNameRenderer,
          forecastAmountRenderer: ForecastAmountRenderer,
          forecastStageRenderer: ForecastStageRenderer,
          forecastCategoryRenderer: ForecastCategoryRenderer,
          forecastCloseDateRenderer: ForecastCloseDateRenderer,
          moneyRenderer: MoneyRenderer,
          numberRenderer: NumberRenderer,
        }}
        columnTypes={{
          dateColumn: {
            filter: 'agDateColumnFilter',
            filterParams: {
              comparator: dateComparator,
            },
          },
        }}
        onColumnRowGroupChanged={this.hanldeOnColumnRowGroupChanged}
        getMainMenuItems={this.getMainMenuItems}
        enableFullScreen
        tableManageConfigs={{
          enableCustomMeta: true,
          validCustomMetaObjects: ['Opportunity'],
        }}
        resourceType={GodTable.RESOURCE_TYPE.OPPORTUNITY}
      />
    );
  }

  render() {
    const {
      opportunities, description,
    } = this.props;
    const { dealType } = this.state;
    const showDescription = description && description.length > 0;
    let filteredOpportunities = opportunities;

    const isFiltering = dealType !== null;

    filteredOpportunities = filter(filteredOpportunities, (o) => {
      let oTypes = o.types || [];
      if (oTypes.length === 0) {
        oTypes = ['Others'];
      }
      const matchedTypes = isFiltering ? (filter(oTypes, t => t === dealType)).length > 0 : true;
      return matchedTypes;
    });

    return (
      <div className={styles.wrapper}>
        {showDescription && <div className="well"><small>{description}</small></div>}
        {opportunities.length > 0 && this.renderCharts()}
        {opportunities.length > 0 && this.renderTable(filteredOpportunities)}
      </div>
    );
  }
}

// Retrieve data from store as props
function mapStateToProps(state, ownProps) {
  return {
    canAccessToSalesforce: getCanAccessToSalesforce(state),
    tableColumns: getTablePreference(state, ownProps.tableName),
  };
}

OpportunityTable.propTypes = {
  opportunities: PropTypes.array.isRequired,
  description: PropTypes.string,
  showStage: PropTypes.bool.isRequired,
  showSalesStage: PropTypes.bool.isRequired,
  canAccessToSalesforce: PropTypes.bool.isRequired,
  isBeginning: PropTypes.bool,
  stats: PropTypes.object.isRequired,
  showTypeChart: PropTypes.bool,
  showCategoryChart: PropTypes.bool,
  showSizeChart: PropTypes.bool,
  showCycleChart: PropTypes.bool,
  dealType: PropTypes.string,
  tableName: PropTypes.string.isRequired,
  isWeighted: PropTypes.bool,
  tableColumns: PropTypes.arrayOf(PropTypes.shape({
    _id: PropTypes.string.isRequired,
    description: PropTypes.string,
    is_system: PropTypes.bool,
    is_visible: PropTypes.bool,
    position: PropTypes.number,
    width: PropTypes.number,
    tag: PropTypes.string,
    label: PropTypes.string,
    target_object: PropTypes.string,
    target_field: PropTypes.string,
  })),
  context: PropTypes.shape({}),
};

OpportunityTable.defaultProps = {
  showStage: false,
  showSalesStage: false,
  showTypeChart: false,
  showCategoryChart: false,
  showSizeChart: false,
  showCycleChart: false,
  dealType: null,
  isBeginning: false,
  tableColumns: [],
  isWeighted: false,
  context: null,
};

export default connect(mapStateToProps)(OpportunityTable);
